import type { ApplyRowResult, ScanRow } from "../../../api/nameTranslationsImport.service.js";

export const PLANNED_HEADERS = [
  "entityType",
  "entityId",
  "entityName",
  "termId",
  "localeCode",
  "translatedName",
] as const;

export const RESULTS_HEADERS = [
  "entityType",
  "entityId",
  "localeCode",
  "status",
  "previous",
] as const;

// Leading characters Excel and Sheets read as a formula; names come from the DB.
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

function csvField(value: string | null): string {
  let text = value === null ? "" : value;
  if (FORMULA_TRIGGERS.some(trigger => text.startsWith(trigger))) {
    text = `'${text}`;
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildPlannedCsv(rows: ScanRow[]): string {
  const lines = [PLANNED_HEADERS.join(",")];
  for (const row of rows) {
    lines.push(
      [row.entityType, row.entityId, row.entityName, row.termId, row.localeCode, row.translatedName]
        .map(csvField)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

export function buildResultsCsv(results: ApplyRowResult[]): string {
  const lines = [RESULTS_HEADERS.join(",")];
  for (const result of results) {
    lines.push(
      [result.entityType, result.entityId, result.localeCode, result.status, result.previous]
        .map(csvField)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

function filenameStamp(now: Date): string {
  return now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function plannedFilename(now: Date): string {
  return `name-translations-import-planned-${filenameStamp(now)}.csv`;
}

export function resultsFilename(now: Date): string {
  return `name-translations-import-results-${filenameStamp(now)}.csv`;
}

function downloadCsv(csv: string, filename: string): void {
  // BOM so Excel opens the CJK names as UTF-8.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Deferred: revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadPlannedCsv(rows: ScanRow[], at: Date): void {
  downloadCsv(buildPlannedCsv(rows), plannedFilename(at));
}

export function downloadResultsCsv(results: ApplyRowResult[], at: Date): void {
  downloadCsv(buildResultsCsv(results), resultsFilename(at));
}
